import {DestroyRef, inject, Injectable} from '@angular/core';
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_TYPE,
} from '@discocil/fv-components';
import {TranslateService} from '@ngx-translate/core';
import {filter, first, mergeMap, Observable, tap, switchMap, catchError, of} from 'rxjs';
import {AccountSocketHandlerService} from 'src/app/core/entities/account/account-socket-handler.service';
import {DeviceStatusService} from 'src/app/core/entities/device/device-status.service';
import {DeviceService} from 'src/app/core/entities/device/device.service';
import {ServicesService} from 'src/app/core/entities/service/services.service';
import {SOCKET_ROOM} from 'src/app/core/enums/events-buffer/socket/socket-rooms-enum';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IDevice} from 'src/app/core/entities/device/models/device.model';
import {SocketConnectionHandlerService} from 'src/app/core/services/socket/socket-connection-handler.service';
import {GlobalConfirmService} from 'src/app/shared/services/global-confirm/global-confirm.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ServiceAuthService {
  private readonly logger = inject(Logger);
  private readonly socketConnectionHandlerService = inject(
    SocketConnectionHandlerService
  );
  private readonly accountSocketHandlerService = inject(
    AccountSocketHandlerService
  );
  private readonly deviceService = inject(DeviceService);
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly translateService = inject(TranslateService);
  private readonly servicesService = inject(ServicesService);
  private readonly deviceStatusService = inject(DeviceStatusService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initServiceFinishedListener();
  }

  /**
   * Listens for the service finished event, retrieves the active device,
   * sends unsent socket events, and opens the service finished confirmation modal.
   *
   * @private
   */
  private initServiceFinishedListener(): void {
    this.servicesService.onServiceFinished$
      .pipe(
        mergeMap(() => this.getActiveDevice()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.accountSocketHandlerService.sendOpenAccountsSyncEvent();
          this.logger.debug(
            `[Socket 🔌] [Room: ${SOCKET_ROOM.service} 🪩] [Current service has been finished]`
          );
          this.openRemovedCurrentServiceConfirm();
          this.socketConnectionHandlerService.sendUnsentSocketEvents();
        },
        error: (error) =>
          this.logger.error(
            new Error(
              '[ServiceAuth][initServiceFinishedListener] Error initializing service finished listener'
            ),
            error
          ),
      });
  }

  /**
   * Opens the confirmation modal indicating that the service has finished.
   * Once the button is clicked, it checks the device status to navigate to the service page.
   *
   * @private
   */
  private openRemovedCurrentServiceConfirm(): void {
    this.globalConfirmService.open({
      noClose: true,
      icon: 'fas fa-door-open',
      iconSize: 'text-3xl',
      message: this.translateService.instant(
        'fv_confirm.current_service_removed.title'
      ),
      hideCloseIcon: true,
      buttons: [
        {
          text: this.translateService.instant('global.understood'),
          color: BUTTON_COLOR.primary,
          size: BUTTON_SIZE.full,
          type: BUTTON_TYPE.filled,
          preventAutoClose: true,
          handler: (): void => {
            this.globalConfirmService.close();
            this.deviceStatusService
              .checkDeviceStatus(false)
              .pipe(first(), takeUntilDestroyed(this.destroyRef))
              .subscribe();
          },
        },
      ],
    });
  }

  /**
   * Listens for the service started event, retrieves the active device,
   * and checks the device status to navigate to the waiters page.
   *
   * @private
   */
  private initServiceStartedListener(): void {
    this.servicesService.onServiceStarted$
      .pipe(
        mergeMap(() => this.getActiveDevice()),
        tap(() => {
          this.logger.debug(
            `[Socket 🔌] [Room: ${SOCKET_ROOM.service} 🪩] [Current service has been started]`
          );
        }),
        switchMap(() => this.deviceStatusService.checkDeviceStatus(true).pipe(
          first(),
          catchError(error => {
            this.logger.error(
              new Error(
                '[ServiceAuth][initServiceStartedListener] Error initializing service started listener'
              ),
              error
            );

            return of(null);
          })
        )),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Retrieves the active device from IndexedDB and filters out inactive devices.
   *
   * @returns An observable that emits the active device.
   * @private
   */
  private getActiveDevice(): Observable<IDevice> {
    return this.deviceService
      .getDevice()
      .pipe(
        filter(
          (device: IDevice | null): device is IDevice =>
            !!device && device.activated
        )
      );
  }
}
